import { useContext, useEffect, useMemo, useState } from 'react';
import Checkbox from '@/FiltersMenu/Checkbox';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import FacetDataContext from '~/contexts/FacetDataContext';
import type { MakeFacet } from '~/contracts';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import ParentModel from './ParentModel';
import LabelWithCount from '../LabelWithCount';
import { ModelWrapper } from '../styles';

// Because there is a delay between toggling a make on and when we start
// fetching facets, we want to delay showing the models. Essentially we are
// waiting for JavaScript processing to happen, which hopefully doesn't longer
// than our wait.
function useSlowShow(shouldShow: boolean, waitMilliseconds: number): boolean {
  const [show, setShow] = useState(shouldShow);

  useEffect(() => {
    let t: number;

    if (shouldShow) {
      setTimeout(() => {
        setShow(true);
      }, waitMilliseconds);
    } else {
      setShow(false);
    }

    return () => {
      clearTimeout(t);
    };
  }, [shouldShow, waitMilliseconds]);

  return show;
}

// When a make is toggled on, we want to wait until the facets are loaded before
// we show the models. We only want to hide models for the most recently toggled
// make.
function useWaitForLoadedOnChanged(
  isSelected: boolean,
  isLoaded: boolean
): boolean {
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (isSelected) setLoading(true);
  }, [isSelected]);

  useEffect(() => {
    if (isLoaded) setLoading(false);
  }, [isLoaded]);

  return !isLoading;
}

type OwnProps = Omit<MakeFacet, 'key'> & {
  make: string;
};

export default function Make({ count, make, parentModels = [] }: OwnProps) {
  const { appliedFilters, updateMakes } = useContext(AppliedFiltersContext);
  const { facetData } = useContext(FacetDataContext);

  const isSelected = useMemo(
    () =>
      appliedFilters?.makes?.some(appliedMake => appliedMake.name === make) ??
      false,
    [appliedFilters.makes, make]
  );

  const hasParentModelCounts = useMemo(
    () => parentModels.some(model => model.count > 0),
    [parentModels]
  );

  const isSelectedLazy = useSlowShow(isSelected, 100);
  const isLoaded = useWaitForLoadedOnChanged(isSelected, !facetData.isLoading);
  const isInExperiment = useCheckExperimentBucket(
    'srch-impossible-facets',
    'experiment'
  );
  const showModels =
    isSelectedLazy && (isLoaded || !isInExperiment) && parentModels.length > 0;

  return (
    <>
      <Checkbox
        facet="make"
        filter={make}
        id={`make-${make}`}
        checked={isSelected}
        onChange={() => updateMakes({ name: make })}
        label={
          <LabelWithCount
            count={count}
            displayName={make}
            isLoading={!isLoaded}
          />
        }
      />
      {showModels && (
        <div className="px-32">
          <p className="mb-16 t-body-s text-grey-5">Models</p>
          <ModelWrapper>
            {parentModels.map(model => (
              <ParentModel
                key={`${make} ${model.key} ${model.modelIds.join('-')}`}
                count={model.count}
                make={make}
                model={model.key}
                showCount={hasParentModelCounts}
              />
            ))}
          </ModelWrapper>
        </div>
      )}
    </>
  );
}
